import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, LogOut, MapPin, Users, CalendarIcon, X, ChevronRight, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

import { api } from "@/lib/pp/api";
import { attemptStats as bdAttemptStats } from "@/lib/pp/bdApi";
import { toLocalYMD, fromYMD } from "@/lib/pp/dates";
import { clearToken, isAuthed } from "@/lib/pp/authSession";
import { CANADIAN_PROVINCES } from "@/lib/pp/constants";
import PharmacyFormDialog from "@/components/pp/PharmacyFormDialog";

const DARK_GREEN = "#0F3B2E";
const LABEL_GREEN = "#2D5A4C";
const TEXT = "#1C2B26";
const MUTED = "#6B7A73";
const WARN = "#D48B47";
const BG = "#F4F3EF";
const BORDER = "#E5E2DC";

const currency = (n) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
  }).format(Number(n || 0));

export default function PPDashboard() {
  const nav = useNavigate();
  // Mode toggle: "partners" (default, existing flow) vs "bd" (business
  // development — hides financial stat cards + swaps row metric + pharmacy
  // detail swaps Financial tracker for BD tracker). Persisted per-session
  // so refreshing the page keeps you in the mode you were browsing.
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "partners";
    return window.sessionStorage.getItem("pp_mode") === "bd" ? "bd" : "partners";
  });
  const isBD = mode === "bd";
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("pp_mode", mode);
    }
  }, [mode]);

  const [tab, setTab] = useState("Active");
  const [items, setItems] = useState([]);
  const [pendingPay, setPendingPay] = useState([]);
  const [reimbursed, setReimbursed] = useState([]);
  const [pendingReg, setPendingReg] = useState([]);
  const [stats, setStats] = useState({
    active_pharmacies: 0,
    inactive_pharmacies: 0,
    fees_pending: 0,
    fees_reimbursed: 0,
    pending_registrations: 0,
    total_patients: 0,
  });
  const [statuses, setStatuses] = useState(["Active", "Inactive"]);
  const [search, setSearch] = useState("");
  const [province, setProvince] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  // Date range filter (top of dashboard)
  const [rangePreset, setRangePreset] = useState("all"); // all|this_month|last_month|last_3_months|ytd|custom
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  // Resolve preset -> {start, end} ISO YYYY-MM-DD (or null,null for "all")
  const dateRange = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    if (rangePreset === "this_month") {
      return { start: toLocalYMD(new Date(y, m, 1)), end: toLocalYMD(new Date(y, m + 1, 0)) };
    }
    if (rangePreset === "last_month") {
      return { start: toLocalYMD(new Date(y, m - 1, 1)), end: toLocalYMD(new Date(y, m, 0)) };
    }
    if (rangePreset === "last_3_months") {
      return { start: toLocalYMD(new Date(y, m - 2, 1)), end: toLocalYMD(new Date(y, m + 1, 0)) };
    }
    if (rangePreset === "ytd") {
      return { start: toLocalYMD(new Date(y, 0, 1)), end: toLocalYMD(now) };
    }
    if (rangePreset === "custom" && customStart && customEnd) {
      return { start: customStart, end: customEnd };
    }
    return { start: null, end: null };
  }, [rangePreset, customStart, customEnd]);

  const [bdCounts, setBdCounts] = useState({}); // pharmacy_id -> total attempts
  const [bdStats, setBdStats] = useState({});   // pharmacy_id -> { total, current_outcome }
  // Filter applied when the user taps a BD summary quadrant. "all" = show
  // every pharmacy (default when Attempts card is active). Otherwise scopes
  // the list to pharmacies whose current_outcome matches.
  const [bdFilter, setBdFilter] = useState("all");

  const loadBdCounts = useCallback(async () => {
    if (!isBD) return;
    try {
      const map = await bdAttemptStats();
      const out = {};
      Object.entries(map || {}).forEach(([pid, s]) => { out[pid] = s.total || 0; });
      setBdCounts(out);
      setBdStats(map || {});
    } catch { /* non-fatal */ }
  }, [isBD]);

  useEffect(() => { loadBdCounts(); }, [loadBdCounts]);

  // Reset the BD quadrant filter when toggling modes so the user starts
  // fresh (showing all pharmacies) each time BD is entered.
  useEffect(() => { setBdFilter("all"); }, [isBD]);

  useEffect(() => {
    if (!isAuthed()) nav("/pharmacy/partners", { replace: true });
  }, [nav]);

  const load = useCallback(async () => {
    const rangeParams =
      dateRange.start && dateRange.end
        ? { start: dateRange.start, end: dateRange.end }
        : {};
    // Fire all endpoints in parallel but never let ONE failure poison the batch.
    const results = await Promise.allSettled([
      api.get("/pharmacies/stats", { params: rangeParams }),
      api.get("/pharmacies", {
        params: {
          // In BD mode we show ALL pharmacies regardless of Active/Inactive
          // status (leads may not be Active yet). Partners mode filters by
          // the currently-selected tab.
          status: !isBD && ["Active", "Inactive"].includes(tab) ? tab : undefined,
          province: province !== "all" ? province : undefined,
          search: search || undefined,
        },
      }),
      api.get("/pharmacies/pending-payments", { params: rangeParams }),
      api.get("/pharmacies/reimbursed-payments", { params: rangeParams }),
      api.get("/pharmacies/pending-registrations"),
      api.get("/settings/statuses"),
    ]);
    const dataOf = (i, fallback) =>
      results[i].status === "fulfilled" ? results[i].value.data : fallback;
    const statsData = dataOf(0, {});
    const pharmacies = dataOf(1, { items: [] });
    const pPay = dataOf(2, { items: [] });
    const reim = dataOf(3, { items: [] });
    const pReg = dataOf(4, { items: [] });
    const sts = dataOf(5, { values: ["Active", "Inactive"] });

    setStats({
      ...statsData,
      pending_registrations: (pReg.items || []).length,
      total_patients: (reim.items || []).length + (pPay.items || []).length,
    });
    setItems(pharmacies.items || []);
    setPendingPay(pPay.items || []);
    setReimbursed(reim.items || []);
    setPendingReg(pReg.items || []);
    setStatuses(sts.values || ["Active", "Inactive"]);

    // Only toast if EVERY single endpoint failed (network offline / total outage).
    const allFailed = results.every((r) => r.status === "rejected");
    if (allFailed) {
      // Silent one-shot retry to survive a cold-start blip
      await new Promise((r) => setTimeout(r, 600));
      try {
        await api.get("/settings/statuses");
      } catch {
        toast.error("Failed to load");
      }
    } else {
      // Log any individual failures for observability but don't bother the user
      results.forEach((r, i) => {
        if (r.status === "rejected") {
          console.warn(`[pp-dashboard] endpoint #${i} failed`, r.reason?.response?.status || r.reason?.message);
        }
      });
    }
  }, [tab, province, search, dateRange.start, dateRange.end, isBD]);

  useEffect(() => {
    load();
  }, [load]);

  const addStatus = async (v) => {
    await api.post("/settings/statuses", { value: v });
    const { data } = await api.get("/settings/statuses");
    setStatuses(data.values);
  };

  const grouped = useMemo(() => {
    // Partners view hides BD-only leads (no membership_date yet). BD view
    // shows every pharmacy — both signed partners and open leads — and
    // narrows down when a summary quadrant filter is active.
    let filtered = isBD
      ? items
      : items.filter((p) => !!p.membership_date);
    if (isBD && bdFilter !== "all") {
      filtered = filtered.filter(
        (p) => (bdStats[p.id]?.current_outcome || null) === bdFilter,
      );
    }
    const by = {};
    for (const p of filtered) {
      const key = p.province || "—";
      (by[key] = by[key] || []).push(p);
    }
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [items, isBD, bdFilter, bdStats]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    clearToken();
    nav("/pharmacy/partners", { replace: true });
  };

  const totalPartners = stats.active_pharmacies + stats.inactive_pharmacies;

  return (
    <div
      data-testid="pp-dashboard"
      style={{
        minHeight: "100vh",
        background: BG,
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          background: "#FFFFFF",
          borderBottom: `1px solid ${BORDER}`,
          padding: "18px 20px",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.28em",
                fontWeight: 600,
                textTransform: "uppercase",
                color: LABEL_GREEN,
                marginBottom: 4,
              }}
            >
              Reconciliation
            </div>
            <h1
              style={{
                fontFamily: "'Work Sans', system-ui, sans-serif",
                fontSize: 26,
                fontWeight: 700,
                color: TEXT,
                margin: 0,
                letterSpacing: "-0.01em",
              }}
            >
              Partner-Pharmacies
            </h1>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {/* Mode toggle — Partners | BD (business development) */}
            <div
              role="tablist"
              aria-label="Mode"
              data-testid="pp-mode-toggle"
              style={{
                display: "inline-flex",
                padding: 3,
                background: "#F0EEE8",
                borderRadius: 999,
                border: `1px solid ${BORDER}`,
              }}
            >
              {[
                { key: "partners", label: "Partners" },
                { key: "bd", label: "BD" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={mode === opt.key}
                  onClick={() => setMode(opt.key)}
                  data-testid={`pp-mode-${opt.key}`}
                  style={{
                    border: "none",
                    background: mode === opt.key ? DARK_GREEN : "transparent",
                    color: mode === opt.key ? "white" : MUTED,
                    padding: "7px 16px",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={logout}
              data-testid="pp-logout-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                border: "none",
                color: TEXT,
                fontSize: 15,
                fontWeight: 500,
                cursor: "pointer",
                padding: "8px 4px",
              }}
            >
              <LogOut style={{ width: 18, height: 18 }} strokeWidth={1.8} />
              Lock
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "22px 20px 60px" }}>
        {/* Date range filter */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              letterSpacing: "0.22em",
              fontWeight: 600,
              textTransform: "uppercase",
              color: LABEL_GREEN,
            }}
          >
            Showing
          </span>
          <Select value={rangePreset} onValueChange={setRangePreset}>
            <SelectTrigger
              className="h-10 bg-white rounded-lg border-[#E5E2DC] min-w-[180px]"
              data-testid="pp-range-preset"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="this_month">This month</SelectItem>
              <SelectItem value="last_month">Last month</SelectItem>
              <SelectItem value="last_3_months">Last 3 months</SelectItem>
              <SelectItem value="ytd">Year to date</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>

          {rangePreset === "custom" && (
            <>
              <Popover open={startOpen} onOpenChange={setStartOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="pp-range-start"
                    className="h-10 inline-flex items-center gap-1.5 bg-white border border-[#E5E2DC] rounded-lg px-3 text-sm text-[#1C2B26]"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 text-[#4A5D55]" />
                    {customStart ? format(new Date(customStart), "MMM d, yyyy") : "Start"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={customStart ? new Date(customStart) : undefined}
                    onSelect={(d) => {
                      setCustomStart(d ? d.toISOString().slice(0, 10) : null);
                      setStartOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
              <Popover open={endOpen} onOpenChange={setEndOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="pp-range-end"
                    className="h-10 inline-flex items-center gap-1.5 bg-white border border-[#E5E2DC] rounded-lg px-3 text-sm text-[#1C2B26]"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 text-[#4A5D55]" />
                    {customEnd ? format(fromYMD(customEnd), "MMM d, yyyy") : "End"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={fromYMD(customEnd)}
                    onSelect={(d) => {
                      setCustomEnd(toLocalYMD(d));
                      setEndOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </>
          )}

          {dateRange.start && dateRange.end && (
            <span
              style={{
                fontSize: 12,
                color: MUTED,
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
              data-testid="pp-range-summary"
            >
              {format(fromYMD(dateRange.start), "MMM d, yyyy")}
              &nbsp;→&nbsp;
              {format(fromYMD(dateRange.end), "MMM d, yyyy")}
              <button
                type="button"
                onClick={() => {
                  setRangePreset("all");
                  setCustomStart(null);
                  setCustomEnd(null);
                }}
                data-testid="pp-range-clear"
                className="inline-flex items-center rounded-full hover:bg-[#E5E2DC]/60 p-0.5"
                aria-label="Clear date range"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
        </div>

        {/* Stat cards — 2x2 grid. Hidden in BD mode; a BD-specific 2x2
            summary appears in its place below. */}
        {!isBD && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 14,
            marginBottom: 22,
          }}
        >
          <StatCard
            label="Active"
            value={stats.active_pharmacies}
            sub={`${totalPartners} pharmac${totalPartners === 1 ? "y" : "ies"}`}
            valueColor={DARK_GREEN}
            testid="pp-stat-active"
            selected={tab === "Active"}
            onClick={() => setTab("Active")}
          />
          <StatCard
            label="Inactive"
            value={stats.inactive_pharmacies}
            sub={`${stats.inactive_pharmacies} pharmac${stats.inactive_pharmacies === 1 ? "y" : "ies"}`}
            valueColor={TEXT}
            testid="pp-stat-inactive"
            selected={tab === "Inactive"}
            onClick={() => setTab("Inactive")}
          />
          <StatCard
            label="Pending"
            value={currency(stats.fees_pending)}
            sub={`${pendingPay.length} payment${pendingPay.length === 1 ? "" : "s"}`}
            valueColor={WARN}
            testid="pp-stat-pending"
            selected={tab === "pending_pay"}
            onClick={() => setTab("pending_pay")}
          />
          <StatCard
            label="Reimbursed"
            value={currency(stats.fees_reimbursed)}
            sub={`${reimbursed.length} payment${reimbursed.length === 1 ? "" : "s"}`}
            valueColor={DARK_GREEN}
            testid="pp-stat-reimbursed"
            selected={tab === "reimbursed"}
            onClick={() => setTab("reimbursed")}
          />
        </div>
        )}

        {/* Pending registrations — full-width secondary button. Hidden in BD mode. */}
        {!isBD && (
        <button
          type="button"
          onClick={() => setTab(tab === "pending_reg" ? "Active" : "pending_reg")}
          data-testid="pp-pending-reg-btn"
          aria-pressed={tab === "pending_reg"}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: tab === "pending_reg" ? "#FFF8ED" : "#FFFFFF",
            border: `1.5px solid ${tab === "pending_reg" ? "#E9A76C" : BORDER}`,
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 22,
            cursor: "pointer",
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            transition: "border-color .15s, background .15s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "#F0A47C22",
                color: "#C97757",
              }}
            >
              <FileWarning style={{ width: 16, height: 16 }} />
            </span>
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: "0.22em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: LABEL_GREEN,
                }}
              >
                Pending registrations
              </div>
              <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                Patients not yet sent to their pharmacy
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontFamily: "'Work Sans', system-ui, sans-serif",
                fontSize: 20,
                fontWeight: 700,
                color: pendingReg.length > 0 ? "#C97757" : TEXT,
              }}
            >
              {pendingReg.length}
            </span>
            <ChevronRight
              style={{
                width: 16,
                height: 16,
                color: MUTED,
                transform: tab === "pending_reg" ? "rotate(90deg)" : "none",
                transition: "transform .2s",
              }}
            />
          </div>
        </button>
        )}

        {/* BD summary — 4-quadrant counters across all pharmacies. Uses the
            same visual size/padding/fonts as the Partners StatCard so both
            modes look consistent. */}
        {isBD && (() => {
          // Only count pharmacies actually present in the current list so
          // the summary numbers always match the list below (no ghosts from
          // deleted or filtered-out pharmacies).
          let totalAttempts = 0;
          const counts = { Pending: 0, Interested: 0, "Not Interested": 0 };
          items.forEach((p) => {
            const s = bdStats[p.id] || {};
            totalAttempts += s.total || 0;
            const o = s.current_outcome;
            if (o && counts[o] !== undefined) counts[o] += 1;
          });
          const q = [
            { label: "Attempts", value: totalAttempts, color: TEXT, testId: "bd-summary-attempts", filter: "all" },
            { label: "Pending", value: counts["Pending"], color: WARN, testId: "bd-summary-pending", filter: "Pending" },
            { label: "Interested", value: counts["Interested"], color: DARK_GREEN, testId: "bd-summary-interested", filter: "Interested" },
            { label: "Not Interested", value: counts["Not Interested"], color: "#B4351B", testId: "bd-summary-not-interested", filter: "Not Interested" },
          ];
          return (
            <div
              data-testid="bd-summary-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 14,
                marginBottom: 22,
              }}
            >
              {q.map((c) => {
                const selected = bdFilter === c.filter;
                return (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => setBdFilter(c.filter)}
                  aria-pressed={selected}
                  data-testid={c.testId}
                  style={{
                    background: "#FFFFFF",
                    border: `1.5px solid ${selected ? DARK_GREEN : BORDER}`,
                    borderRadius: 14,
                    padding: "16px 18px",
                    minHeight: 128,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    textAlign: "left",
                    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                    cursor: "pointer",
                    transition: "border-color .15s, box-shadow .15s",
                    boxShadow: selected ? `0 0 0 3px ${DARK_GREEN}20` : "none",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      letterSpacing: "0.14em",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: LABEL_GREEN,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "'Work Sans', system-ui, sans-serif",
                      fontSize: 34,
                      fontWeight: 700,
                      color: c.color,
                      margin: "8px 0",
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                    }}
                  >
                    {c.value}
                  </div>
                  <div style={{ fontSize: 13, color: MUTED }}>
                    {c.label === "Attempts" ? "total attempts" : "pharmacies"}
                  </div>
                </button>
                );
              })}
            </div>
          );
        })()}

        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 18,
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 260px", minWidth: 220 }}>
            <Search
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                width: 16,
                height: 16,
                color: MUTED,
              }}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pharmacy by name"
              className="pl-11 bg-white h-12 rounded-lg border-[#E5E2DC]"
              data-testid="pp-search-input"
            />
          </div>
          <Select value={province} onValueChange={setProvince}>
            <SelectTrigger
              className="flex-1 min-w-[140px] h-12 bg-white rounded-lg border-[#E5E2DC]"
              data-testid="pp-province-filter"
            >
              <SelectValue placeholder="All provinces" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All provinces</SelectItem>
              {CANADIAN_PROVINCES.map((p) => (
                <SelectItem key={p.code} value={p.code}>
                  {p.code} — {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            data-testid="pp-add-pharmacy-btn"
            className="h-12 inline-flex items-center gap-1.5 rounded-lg bg-[#0F3B2E] hover:bg-[#0A2A20] text-white font-semibold text-sm px-4 whitespace-nowrap transition-colors"
            style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
          >
            <Plus style={{ width: 16, height: 16 }} strokeWidth={2.2} />
            Add pharmacy
          </button>
        </div>

        {/* Content */}
        {(tab === "Active" || tab === "Inactive") && (
          <>
            {grouped.length === 0 && (
              <p style={{ fontSize: 14, color: MUTED, marginTop: 8 }}>
                No pharmacies match your filters.
              </p>
            )}
            {grouped.map(([prov, list]) => (
              <div key={prov} style={{ marginBottom: 28 }}>
                <h3
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    letterSpacing: "0.24em",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: LABEL_GREEN,
                    margin: "0 0 10px",
                  }}
                >
                  <MapPin style={{ width: 14, height: 14 }} />
                  {prov}
                  <span style={{ color: MUTED, fontWeight: 400, letterSpacing: 0 }}>
                    · {list.length}
                  </span>
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
                    gap: 12,
                  }}
                >
                  {list.map((p) => (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card
                        className="cursor-pointer hover:border-[#0F3B2E]/50 transition-colors"
                        onClick={() => nav(`/pharmacy/partners/${p.id}${isBD ? "?mode=bd" : ""}`)}
                        data-testid={`pp-card-${p.id}`}
                      >
                        <CardContent className="p-5 space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 style={{ fontWeight: 600, color: TEXT, margin: 0 }}>
                                {p.name}
                              </h4>
                              <p style={{ fontSize: 12, color: MUTED, margin: "2px 0 0" }}>
                                {[p.city, p.province].filter(Boolean).join(", ")}
                              </p>
                            </div>
                            <Badge
                              className={
                                p.status === "Active"
                                  ? "bg-[#3B7A57] text-white"
                                  : "bg-[#E5E2DC] text-[#4A5D55]"
                              }
                            >
                              {p.status}
                            </Badge>
                          </div>
                          <div style={{ display: "flex", gap: 12, fontSize: 12, color: MUTED }}>
                            {isBD ? (
                              <span
                                data-testid={`pp-card-${p.id}-attempts`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  color: LABEL_GREEN,
                                  background: "#E5EFEA",
                                  padding: "3px 10px",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                }}
                              >
                                {bdCounts[p.id] || 0} attempt{(bdCounts[p.id] || 0) === 1 ? "" : "s"}
                              </span>
                            ) : (
                              <>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <Users style={{ width: 13, height: 13 }} />
                                  {p.patient_count}
                                </span>
                                <span style={{ color: WARN }}>{p.pending_registrations} pending reg</span>
                                <span style={{ color: "#B84A4A" }}>{p.pending_payments} pending pay</span>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        {tab === "pending_pay" && <PatientRows items={pendingPay} nav={nav} />}
        {tab === "reimbursed" && <PatientRows items={reimbursed} nav={nav} />}
        {tab === "pending_reg" && <PatientRows items={pendingReg} nav={nav} emptyLabel="No pending registrations." />}
      </div>

      <PharmacyFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        editing={editing}
        statuses={statuses}
        onAddStatus={addStatus}
        onSaved={() => { load(); loadBdCounts(); }}
        mode={isBD ? "bd" : "partners"}
      />
    </div>
  );
}

const StatCard = ({ label, value, sub, valueColor, testid, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={selected}
    style={{
      background: "#FFFFFF",
      border: `1.5px solid ${selected ? DARK_GREEN : BORDER}`,
      borderRadius: 14,
      padding: "16px 18px",
      minHeight: 128,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      textAlign: "left",
      cursor: "pointer",
      transition: "border-color .15s, box-shadow .15s",
      boxShadow: selected ? `0 0 0 3px ${DARK_GREEN}20` : "none",
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      width: "100%",
    }}
  >
    <div
      style={{
        fontSize: 12,
        letterSpacing: "0.22em",
        fontWeight: 600,
        textTransform: "uppercase",
        color: LABEL_GREEN,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: "'Work Sans', system-ui, sans-serif",
        fontSize: 34,
        fontWeight: 700,
        color: valueColor,
        margin: "8px 0",
        letterSpacing: "-0.02em",
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: 13, color: MUTED }}>{sub}</div>
  </button>
);

const Tab = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    style={{
      whiteSpace: "nowrap",
      padding: "10px 18px",
      borderRadius: 999,
      background: active ? DARK_GREEN : "transparent",
      color: active ? "#FFFFFF" : TEXT,
      border: active ? "none" : `1px solid transparent`,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      transition: "background-color .15s",
    }}
  >
    {children}
  </button>
);

const PatientRows = ({ items, nav, emptyLabel }) => {
  if (!items.length)
    return <p style={{ fontSize: 14, color: MUTED }}>{emptyLabel || "Nothing pending."}</p>;

  const monthLabel = (m) => {
    if (!m) return "—";
    const s = String(m).trim();
    if (/^all$/i.test(s)) return "All";
    return `M${s}`;
  };

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${BORDER}`,
        borderRadius: 14,
        overflow: "hidden",
      }}
    >
      {items.map((it, idx) => {
        // Pending-registrations items ARE patients (id = patient_id).
        // Pending-pay / reimbursed items are RECORDS (patient_id is a separate field).
        const patientId = it.patient_id || it.id;
        return (
          <div
            key={it.id}
            onClick={() =>
              nav(`/pharmacy/partners/${it.pharmacy_id}/patients/${patientId}`)
            }
            data-testid={`pp-pending-row-${it.id}`}
            style={{
              cursor: "pointer",
              borderTop: idx === 0 ? "none" : `1px solid ${BORDER}`,
              padding: "12px 14px",
              fontSize: 14,
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTapHighlightColor: "transparent",
            }}
            className="hover:bg-[#F9F8F6]"
          >
          {/* Line 1 — Client ID + patient name on the left, fee on the right */}
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 10,
              minWidth: 0,
            }}
          >
            <div
              style={{
                minWidth: 0,
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  color: TEXT,
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                {it.client_id || "—"}
              </span>
              <span
                style={{
                  color: TEXT,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {it.patient_name || "—"}
              </span>
            </div>
            {it.fee != null && (
              <span
                style={{
                  fontWeight: 600,
                  color: TEXT,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                  flexShrink: 0,
                }}
              >
                {currency(it.fee)}
              </span>
            )}
          </div>

          {/* Line 2 — pharmacy · medication · month (subtle) */}
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: MUTED,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {[
              it.pharmacy_name,
              it.medication,
              it.months ? monthLabel(it.months) : null,
            ]
              .filter((x) => x && x !== "—")
              .join(" · ")}
          </div>
        </div>
        );
      })}
    </div>
  );
};

const thCss = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 11,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: MUTED,
};
const tdCss = { padding: "10px 14px" };
